/**
 * markdown-it plugin that transforms standard `![alt](url)` image syntax
 * into responsive `<picture>` elements when the URL matches the DCS CDN
 * asset pattern.
 *
 * Non-CDN images are rendered with the default image renderer (plain `<img>`).
 *
 * @example
 * ```ts
 * // .vitepress/config.ts
 * import { responsiveImagePlugin } from '@duffcloudservices/cms/plugins'
 *
 * export default defineConfig({
 *   markdown: {
 *     config: (md) => {
 *       md.use(responsiveImagePlugin)
 *     },
 *   },
 * })
 * ```
 *
 * Input markdown:
 * ```md
 * ![Physical therapy session](https://files.duffcloudservices.com/kept/assets/blog/abc-123.jpg)
 * ```
 *
 * Rendered HTML:
 * ```html
 * <picture>
 *   <source srcset="...abc-123-sm.webp 640w, ...abc-123-md.webp 1024w, ...abc-123-lg.webp 1920w"
 *           type="image/webp"
 *           sizes="(max-width: 1024px) 100vw, 1024px" />
 *   <img src="...abc-123-md.webp" alt="Physical therapy session" loading="lazy" decoding="async" />
 * </picture>
 * ```
 */

import type MarkdownIt from 'markdown-it'

/**
 * Matches DCS CDN asset URLs: base path / UUID . extension.
 *
 * Aligned with `@duffcloudservices/cms-core` `responsive-image.ts`: the
 * `(?:content\/)?` group covers the content-container shape
 * `https://files.duffcloudservices.com/content/{slug}/assets/...` in addition
 * to the branding-container shape `.../{slug}/assets/...`. The group is
 * non-capturing, so the captured base path / UUID / extension are unchanged.
 */
const CDN_PATTERN = /^(https?:\/\/files\.[^/]+\/(?:content\/)?[^/]+\/assets\/(?:[^/]+\/)*)([a-f0-9-]+)\.(\w+)$/

/**
 * Escapes HTML special characters for safe attribute embedding.
 * Falls back to a simple replace chain when `md.utils.escapeHtml` is unavailable.
 */
function escapeHtml(str: string): string {
  return str
    .replace(/&/g, '&amp;')
    .replace(/"/g, '&quot;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
}

export function responsiveImagePlugin(md: MarkdownIt): void {
  const defaultImageRenderer = md.renderer.rules.image

  md.renderer.rules.image = (tokens, idx, options, env, self) => {
    const token = tokens[idx]
    const src = token.attrGet('src') ?? ''
    const alt = token.content ?? ''

    const match = src.match(CDN_PATTERN)
    if (!match) {
      // Not a CDN image — render normally
      if (defaultImageRenderer) {
        return defaultImageRenderer(tokens, idx, options, env, self)
      }
      return self.renderToken(tokens, idx, options)
    }

    const [, basePath, uuid] = match

    const srcset = [
      `${basePath}${uuid}-sm.webp 640w`,
      `${basePath}${uuid}-md.webp 1024w`,
      `${basePath}${uuid}-lg.webp 1920w`,
    ].join(', ')

    const escapedAlt = escapeHtml(alt)

    // Self-heal: this plugin emits a STATIC HTML string at build time (no Vue
    // runtime), so the fallback must be an inline onerror. A fit-variant
    // (`-md`/`-lg`) may legitimately be absent (internal/imaging variants.go
    // skips variants by DIMENSION). On the first error, swap the `<img>` to the
    // ORIGINAL url AND strip the sibling `<source>` elements — a `<picture>`
    // does NOT re-resolve after a `<source>` 404s, so the variant set must be
    // removed for the original to display. (`this.onerror=null` prevents loops.)
    const escapedOriginal = escapeHtml(src)
    const onError =
      `this.onerror=null;this.src='${escapedOriginal}';` +
      `this.closest('picture')?.querySelectorAll('source').forEach(s=>s.remove())`

    return [
      '<picture>',
      `  <source srcset="${srcset}" type="image/webp" sizes="(max-width: 1024px) 100vw, 1024px" />`,
      `  <img src="${basePath}${uuid}-md.webp" alt="${escapedAlt}" loading="lazy" decoding="async" onerror="${onError}" />`,
      '</picture>',
    ].join('\n')
  }
}
